import { PropType, computed, defineComponent, onUnmounted, ref, watch } from 'vue'
import { Store } from 'pinia'
import { getChargingTripRouteCDN } from './recommend.api'
import { gcj02ToBd09 } from '@/utils/coordinateTransform'
import { highwayRouteColor, zoomToRatio } from './utils'
import { ChargeMapContext } from '../chargerMap/index.model'
import { ComponentActions, ComponentState } from '../../nsh5/localStores/component'
import { RouteCDNModel } from '../../models/detail.model'
import { Line, MapViewMode, Point, PowerRoute } from '../../contract/map'
import { calcSize, drawPointCountry, resizeMapResources } from '../chargerMap/mapUtils'
import { DEFAULT_CITY_ZOOM_LEVEL } from '../../config/zoomLevelConfig'

/**
 * 路线渲染功能模块
 */
export default defineComponent({
  name: 'route-render-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
    cdnUrl: {
      type: RouteCDNModel,
      default: null,
    },
  },

  setup: (props) => {
    const mapContextRef = computed(() => props.mapContextRef)
    const mapInstance = computed(() => props.mapInstance)
    // const mapContextRef = ref<ChargeMapContext>()
    // const mapInstance = ref<any>()
    const zoomLevel = ref<number>(0)
    let timer: any
    let recommendRoutes: PowerRoute[] = [] // 推荐路线
    let highwayTag = '' // 区分高速的标签名
    const routeResources: any = {
      // 路线上的资源点
      swap: [] as Point[],
      charge: [] as Point[],
    }

    let hasDrawRecommendPoint = false // 是否已经绘制推荐路线

    /**
     * 加载线路数据
     * @param cdnUrl
     */
    const getRecommendRoute = async (cdnUrlRes: any) => {
      const tripRoutes = await getChargingTripRouteCDN(cdnUrlRes.chargingTripRouteLink)
      const routes: PowerRoute[] = []
      if (tripRoutes && tripRoutes.length) {
        for (const tripRoute of tripRoutes) {
          const mapData = tripRoute.mapData
          const polyline = mapData.polyline
          const polylineRgeo = mapData.polylineRgeo // 起点终点的名称，用于画起点终点
          const route: PowerRoute = {
            id: tripRoute.id,
            description: tripRoute?.detail?.description,
            polyline: polyline.map((location: any) => {
              const position = location.split(',')
              return { lng: Number(position[0]), lat: Number(position[1]) }
            }),
            locations:
              polylineRgeo && polylineRgeo.length
                ? [polylineRgeo[0], polylineRgeo[polylineRgeo.length - 1]]
                : [], // 画起点终点的城市名称
            tags: tripRoute?.detail?.tags,
            resources: mapData?.resources || [],
            detail: tripRoute,
            distance: mapData.distance,
          }
          routes.push(route)
        }
      }
      recommendRoutes = routes
      props.componentStore.$patch({ recommendRoutes })
    }

    /**
     * 推荐路线全国绘制
     * @param {MapContext} context 地图上下文
     * @param {PowerRoute[]} recommendRoutes 推荐路线数据
     * @param {string} highwayTag 区分高速的标签名
     * @param {number} ratio 不同zoom级别缩放比例
     */
    const drawRecommendRoute = async (
      context: ChargeMapContext,
      recommendRoutes: PowerRoute[],
      highwayTag?: string,
      ratio = 0.8
    ) => {
      if (recommendRoutes && recommendRoutes.length) {
        const drawPolyline: Line[] = []
        recommendRoutes.forEach((item: PowerRoute) => {
          const color = highwayRouteColor(item.tags, highwayTag)
          drawPolyline.push({
            points: item.polyline.map((pi: Point) => {
              return gcj02ToBd09({
                lng: Number(pi.lng),
                lat: Number(pi.lat),
              })
            }),
            options: {
              strokeWeight: 1 * ratio,
              strokeColor: color,
            },
          } as Line)
        })
        // 画线
        context?.addPolylineMapvgl('highway', drawPolyline) // 使用mapvgl方式
      }
    }

    /**
     * 随zoom变化改变路线宽度
     */
    const changeRouteWeight = () => {
      recommendRoutes &&
        drawRecommendRoute(
          mapContextRef.value!,
          recommendRoutes,
          highwayTag,
          zoomToRatio(zoomLevel.value)
        )
    }

    /**
     * 初始绘制推荐路线
     */
    const drawRecommendRoutePolyline = () => {
      if (!highwayTag) {
        highwayTag = props.componentStore.dictionaries?.filter((item) => {
          return item.key === 'h5_recommend_route_high_speed_config'
        })[0]?.value
      }
      drawRecommendRoute(mapContextRef.value!, recommendRoutes, highwayTag)
      if (props.componentStore.viewMode === MapViewMode.CountyLayout) {
        // 20240417 县县通模式打开就不要显示线
        mapContextRef.value?.operatePolylineLayer('highway', false)
      }
    }

    /**
     * 画推荐线路上的点
     */
    const drawRecommendRouteResources = () => {
      if (recommendRoutes && recommendRoutes.length) {
        recommendRoutes.forEach((route: PowerRoute) => {
          route.resources.forEach((resource) => {
            const position = resource.location?.split(',')
            if (resource.type === 'PowerSwap') {
              routeResources.swap.push(
                gcj02ToBd09({
                  lng: Number(position[0]),
                  lat: Number(position[1]),
                })
              )
            } else {
              routeResources.charge.push(
                gcj02ToBd09({
                  lng: Number(position[0]),
                  lat: Number(position[1]),
                })
              )
            }
          })
        })
      }
      drawPointCountry(mapContextRef.value!, routeResources.charge, 'charge', 'recommendCharge')
      drawPointCountry(mapContextRef.value!, routeResources.swap, 'swap', 'recommendSwap')
      hasDrawRecommendPoint = true
    }

    // 优先让地图加载和渲染完成后再请求数据
    const mapReadyRequestData = async (cdnUrlRes: any) => {
      await getRecommendRoute(cdnUrlRes) // 获取全国换电站资源
      drawRecommendRoutePolyline()
    }

    const initRouteData = () => {
      // 请求cdn资源
      timer && clearInterval(timer)
      let index = 0
      timer = setInterval(() => {
        if (props.cdnUrl) {
          clearInterval(timer)
          mapReadyRequestData(props.cdnUrl)
        } else {
          index++
        }
        if (index > 6) {
          clearInterval(timer)
        }
      }, 1000)
    }

    // ------------暴露出去的方法--------------
    const zoomEnd = async () => {
      zoomLevel.value = mapInstance.value?.getZoom()
      if (
        props.componentStore.viewMode === MapViewMode.Recommend ||
        props.componentStore.viewMode === MapViewMode.Init
      ) {
        changeRouteWeight()
        resizeMapResources(mapContextRef.value!, calcSize(zoomLevel.value).w) // 调整点的大小
        if (
          props.componentStore.viewMode === MapViewMode.Init &&
          zoomLevel.value > DEFAULT_CITY_ZOOM_LEVEL
        ) {
          mapContextRef.value?.operatePolylineLayer('highway', false)
        }
      }
    }

    // 启动相关事件监听
    const initEventListener = () => {
      mapInstance.value?.addEventListener('zoomend', zoomEnd, false)
    }

    // // unmounted时摧毁相关事件监听
    // const destroyEventListener = () => {
    //   mapInstance.value?.removeEventListener('zoomend', zoomEnd, false)
    // }

    onUnmounted(() => {
      // destroyEventListener()
    })

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = () => {
      // mapInstance.value = mapContextRef.value?.getMap()
      initEventListener()
      initRouteData()
    }
    watch(
      () => props.mapContextRef,
      () => {
        if (props.mapContextRef) {
          // mapContextRef.value = props.mapContextRef
          onReady()
        }
      },
      {
        immediate: true,
      }
    )

    watch(
      () => props.componentStore.viewMode,
      () => {
        if (props.componentStore.viewMode === MapViewMode.Recommend) {
          props.componentStore.unsetDestLocationByPin() // 清除气泡
          mapContextRef.value?.operatePolylineLayer('highway', true)
          if (hasDrawRecommendPoint) {
            mapContextRef.value?.operatePointLayer('recommendSwap', true)
            mapContextRef.value?.operatePointLayer('recommendCharge', true)
          } else {
            // 路线上的点图层只创建一次
            drawRecommendRouteResources()
          }
        } else {
          mapContextRef.value?.operatePointLayer('recommendSwap', false)
          mapContextRef.value?.operatePointLayer('recommendCharge', false)
          if (props.componentStore.viewMode != MapViewMode.Init) {
            mapContextRef.value?.operatePolylineLayer('highway', false)
          } else {
            if (zoomLevel.value > DEFAULT_CITY_ZOOM_LEVEL) {
              mapContextRef.value?.operatePolylineLayer('highway', false)
            } else {
              mapContextRef.value?.operatePolylineLayer('highway', true)
            }
          }
        }
      }
    )
    return () => {}
  },
})
